import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, CheckCheck, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { API, toast } from '@/App';
import {
  formatNotificationRelativeTime,
  isNotificationRead,
  resolveNotificationNavigation,
} from '@/utils/notifications';

const NOTIFICATION_ICONS = {
  payment: '💳',
  payment_request: '🧾',
  payment_approved: '✅',
  payment_rejected: '⛔',
  delivery_assigned: '🚚',
  delivery_status_changed: '📦',
  status_update: '📦',
  new_delivery: '🆕',
  mission_assigned: '🧭',
  delivery_completed: '✅',
  wallet_credit: '💰',
  wallet_debit: '💸',
  wallet_adjustment: '🪙',
  wallet_recharge_pending: '⌛',
  wallet_recharge_confirmed: '💰',
  wallet_recharge_rejected: '⛔',
  kyc_pending_review: '🛡️',
  kyc_approved: '🛡️',
  kyc_rejected: '⚠️',
  ambassador_application_submitted: '📣',
  system: '⚙️',
  promotion: '🎉',
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clock, setClock] = useState(() => Date.now());

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setClock(Date.now());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notifications`, {
        params: { limit: 10 },
      });
      const nextNotifications = Array.isArray(response.data) ? response.data : [];
      setNotifications(nextNotifications);
      await loadUnreadCount(nextNotifications);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async (fallbackNotifications = null) => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(Number(response.data?.count) || 0);
    } catch (error) {
      console.error('Error loading unread count:', error);
      if (Array.isArray(fallbackNotifications)) {
        setUnreadCount(fallbackNotifications.filter((item) => !isNotificationRead(item)).length);
      }
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const response = await axios.post(`${API}/notifications/${notificationId}/read`);
      const updatedNotification = response.data?.notification;
      setNotifications((currentNotifications) =>
        currentNotifications.map((item) =>
          item.id === notificationId
            ? {
                ...item,
                ...(updatedNotification || {}),
                read: true,
                is_read: true,
              }
            : item
        )
      );
      if (typeof response.data?.unread_count === 'number') {
        setUnreadCount(Math.max(0, response.data.unread_count));
      } else {
        setUnreadCount((currentCount) => Math.max(0, currentCount - 1));
      }
      return updatedNotification;
    } catch (error) {
      toast.error('Erreur lors de la mise a jour');
      return null;
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`);
      setNotifications((currentNotifications) =>
        currentNotifications.map((item) => ({
          ...item,
          read: true,
          is_read: true,
        }))
      );
      setUnreadCount(0);
      toast.success('Toutes les notifications ont ete marquees comme lues');
    } catch (error) {
      toast.error('Erreur lors de la mise a jour');
    }
  };

  const deleteNotification = async (notificationId, event) => {
    event.stopPropagation();
    try {
      const deletedNotification = notifications.find((item) => item.id === notificationId);
      await axios.delete(`${API}/notifications/${notificationId}`);
      setNotifications((currentNotifications) => currentNotifications.filter((item) => item.id !== notificationId));
      if (deletedNotification && !isNotificationRead(deletedNotification)) {
        setUnreadCount((currentCount) => Math.max(0, currentCount - 1));
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleNotificationClick = async (notification) => {
    const wasRead = isNotificationRead(notification);
    let effectiveNotification = notification;

    if (!wasRead) {
      const updatedNotification = await markAsRead(notification.id);
      if (!updatedNotification) {
        return;
      }
      effectiveNotification = {
        ...notification,
        ...updatedNotification,
      };
    }

    const { route, message } = resolveNotificationNavigation(effectiveNotification);
    if (!route) {
      toast.error("Aucune page n'est disponible pour cette notification.");
      return;
    }

    if (message) {
      if (typeof toast.warning === 'function') {
        toast.warning(message);
      } else {
        toast.error(message);
      }
    }

    setIsOpen(false);
    navigate(route);
  };

  const getNotificationIcon = (type) => NOTIFICATION_ICONS[type] || '🔔';

  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={(nextOpen) => {
        setIsOpen(nextOpen);
        if (nextOpen) {
          loadNotifications();
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="notification-bell-trigger"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <Badge
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white text-xs"
              data-testid="notification-bell-badge"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 max-h-[500px] overflow-y-auto" data-testid="notification-dropdown">
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="text-xs h-7"
              data-testid="notification-mark-all-read"
            >
              <CheckCheck className="w-3 h-3 mr-1" />
              Tout marquer comme lu
            </Button>
          )}
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="spinner mx-auto"></div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Bell className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Aucune notification</p>
          </div>
        ) : (
          <>
            {notifications.map((notification) => {
              const notificationRead = isNotificationRead(notification);
              return (
                <DropdownMenuItem
                  key={notification.id}
                  className={`p-0 focus:bg-transparent ${!notificationRead ? 'bg-blue-50' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                  data-testid={`notification-item-${notification.id}`}
                >
                  <div className="w-full p-3 hover:bg-gray-50 cursor-pointer relative group">
                    <div className="flex items-start gap-3">
                      <div className="text-2xl flex-shrink-0">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-semibold text-sm leading-tight">
                            {notification.title}
                          </p>
                          {!notificationRead && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1" />
                          )}
                        </div>
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-400 mt-1" data-testid={`notification-time-${notification.id}`}>
                          {formatNotificationRelativeTime(notification.created_at, clock)}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6"
                        onClick={(event) => deleteNotification(notification.id, event)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </DropdownMenuItem>
              );
            })}

            <DropdownMenuSeparator />

            <div className="p-2 text-center">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs"
                data-testid="notification-preferences-link"
                onClick={() => {
                  setIsOpen(false);
                  navigate('/account/notifications');
                }}
              >
                Preferences notifications
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
